/**
 * Checks if the provided string is a valid email address.
 *
 * @param {string} value - The string to be checked.
 * @return {boolean} - Returns true if the string is a valid email address, otherwise false.
 */
const isEmail = (value: string): boolean => {
  const pattern =
    /^([a-zA-Z0-9_.+-]+)@((\[[0-9]{1,3}(\.[0-9]{1,3}){3}])|(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}))$/
  return pattern.test(value)
}

const GCS_BASE_URL = (process.env.GOOGLE_CLOUD_STORAGE_URL || '').replace(/\/+$/, '')

const withGcsBase = (url: string | null | undefined) => {
  if (!url) return null
  if (/^https?:\/\//i.test(url)) return url
  if (!GCS_BASE_URL) return url
  return `${GCS_BASE_URL}/${url.replace(/^\/+/, '')}`
}
/**
 * Checks if the provided string consists only of digits.
 *
 * @param {string} value - The string to be checked.
 * @return {boolean} - Returns true if the string consists only of digits, otherwise false.
 */
const isNumeric = (value: string): boolean => {
  const pattern = /^\d+$/

  return pattern.test(value)
}

/**
 * Checks if the provided string is a strong password.
 *
 * @param {string} value - The string to be checked.
 * @return {boolean} - Returns true if the string is a strong password, otherwise false.
 */
const isStrongPassword = (value: string): boolean => {
  const pattern = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z]).{8,16}$/

  return pattern.test(value)
}

/**
 * Formats a number as a currency string in GBP with the symbol.
 *
 * @param {number} value - The number to be formatted.
 * @return {string} The formatted currency string.
 */
const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    currencyDisplay: 'symbol',
  }).format(value)

/**
 * Formats a number as Rupiah using the site language's digit grouping.
 *
 * @param {number} value - The amount in Rupiah.
 * @param {'en' | 'id'} language - "id" gives "Rp 45.000.000", "en" gives "Rp 45,000,000".
 * @return {string} The formatted amount.
 */
const formatRupiah = (value: number, language: 'en' | 'id'): string =>
  `Rp ${value.toLocaleString(language === 'id' ? 'id-ID' : 'en-US')}`

const ToolBox = {
  isEmail,
  isNumeric,
  formatCurrency,
  formatRupiah,
  isStrongPassword,
  withGcsBase,
}

export default ToolBox
